/* eslint-disable @typescript-eslint/no-non-null-assertion */
import { FC, useContext, useEffect, useState } from 'react';
import styles from './contract.module.scss';
import {
  immediateWarrantyPeriodTypeValues,
  monthsWarrantyPeriodTypeValues,
  notApplicableWarrantyPeriodTypeValues,
  ObraContrato,
  unknownWarrantyPeriodTypeValues
} from '../../../types/ObraDetails';
import { WandaProject } from '../../../types/ObraDetails';
import ContractInfo from './components/contractInfo';
import InfoBox from '../components/infoBox/infoBox';
import fundingPicture from '../../../assets/funding.svg';
import contractPicture from '../../../assets/contract.svg';
import statePicture from '../../../assets/state.svg';
import guaranteePicture from '../../../assets/guarantee.svg';
import { useTranslation } from 'react-i18next';
import { AuthContext } from '../../../services/authContextProvider';
import InformationIcon from '../components/informationIcon/informationIcon';
import ObrasAPI from '../../../services/APIObras';
import ErrorDetails from '../components/ErrorDetails/ErrorDetails';
import { ContractLoadingSkeleton } from './components/contractLoadingSkeleton/contractLoadingSkeleton';

// eslint-disable-next-line @typescript-eslint/no-empty-interface
interface ContractProps {
  codCentro: string;
  wandaProject?: WandaProject;
  wandaProjectLoading: boolean;
  wandaProjectError: boolean;
}

const Contract: FC<ContractProps> = (props) => {
  const { codCentro, wandaProject, wandaProjectLoading, wandaProjectError } = props;
  const {t} = useTranslation();
  const authContext = useContext(AuthContext);
  const [contract, setContract] = useState<ObraContrato>();
  const [loadError, setLoadError] = useState(false);
  const isViewer = !authContext.can('can_read_sensitive');

  useEffect(() => {
    const api = ObrasAPI.getInstance();
    setContract(undefined);
    setLoadError(false);
    if (!wandaProject) return;

    api.getObraContractAPI(codCentro, wandaProject)
      .then((contractResponse) => {
        setContract({
          tipos: contractResponse.tipos || [],
          modelo: contractResponse.modelo || [],
          alcance: contractResponse.alcance || [],
          pago: contractResponse.pago || [],
          fuentesFinanciacion: contractResponse.fuentesFinanciacion || [],
          periodoGarantia: contractResponse.periodoGarantia || '',
          tipoPeriodoGarantia: contractResponse.tipoPeriodoGarantia || '',
          codEstado: contractResponse.codEstado || '',
          nomEstado: contractResponse.nomEstado || '',
        });
      })
      .catch(() => {
        setLoadError(true);
      });
  }, [codCentro, wandaProject]);

  if ((!contract && loadError) || wandaProjectError) return <ErrorDetails />;
  if (!contract || wandaProjectLoading) return <ContractLoadingSkeleton isViewer={isViewer} />;

  let valueGuarantee = t('details.notInformed');
  const warrantyPeriodType = contract.tipoPeriodoGarantia;

  if (warrantyPeriodType) {
    if (immediateWarrantyPeriodTypeValues.includes(warrantyPeriodType.toLowerCase())) {
      valueGuarantee = warrantyPeriodType;
    }

    if (notApplicableWarrantyPeriodTypeValues.includes(warrantyPeriodType.toLowerCase())) {
      valueGuarantee  = t('details.notApplied');
    }

    if (unknownWarrantyPeriodTypeValues.includes(warrantyPeriodType.toLowerCase())) {
      valueGuarantee = t('details.notInformed');
    }

    if (monthsWarrantyPeriodTypeValues.includes(warrantyPeriodType.toLowerCase()) && contract.periodoGarantia !== '0') {
      valueGuarantee = `${contract.periodoGarantia} ${contract.periodoGarantia === '1' ? t('dateMeasures.month') : t('dateMeasures.months')}`;
    }
  }

  return (<div data-testid='contract-test' className={styles.contractContainer}>
    <div id='contract' className={styles.heading}>{t('details.contract.tab')}
      <InformationIcon message={t('help.contract.contract')} />
    </div>
    <div className={styles.boxes}>
      <div className={styles.boxContainer}>
        <ContractInfo
          identifier='contract-properties'
          picture={contractPicture}
          tipo={{ title: t('details.contract.type'), value: contract.tipos?.length > 0 ? contract.tipos.join(' / ') : t('details.notInformed') }}
          modelo={{ title: t('details.contract.model'), value: contract.modelo?.length > 0 ? contract.modelo.join(' / ') : t('details.notInformed') }}
          alcance={{ title: t('details.contract.range'), value: contract.alcance?.length > 0 ? contract.alcance.join(' / ') : t('details.notInformed') }}
          pago={{ title: t('details.contract.payment'), value: contract.pago?.length > 0 ? contract.pago.join(' / ') : t('details.notInformed') }} />
      </div>
      <div className={styles.boxContainer}>
        <div className={styles.boxRow}>
          <div className={isViewer ? styles.stateBox : styles.boxContainerChild}>
            <InfoBox identifier='funding' picture={fundingPicture} item={{ title: t('details.contract.funding'), value: (contract.fuentesFinanciacion?.length > 0) ? contract.fuentesFinanciacion.join(' / ') : t('details.notInformed') }} />
          </div>
          {!isViewer &&
            <div className={styles.boxContainerChild}>
              <InfoBox identifier='guarantee' picture={guaranteePicture} item={{ title: t('details.contract.guarantee'), value: valueGuarantee}} />
            </div>
          }
        </div>
        <div className={styles.boxRow}>
          <div className={styles.stateBox}>
            <InfoBox identifier='state' picture={statePicture} item={{ title: t('details.contract.state'), value: contract.nomEstado || t('details.notInformed') }} />
          </div>
        </div>
      </div>
    </div>
    <div className='footer' style={{marginBottom: '75px'}}></div>
  </div>);
};

export default Contract;
